/**
 * The ⓘ affordance beside a calendar day-header number.
 *
 * A sibling of MetricInfo with the same popover anatomy, reading the
 * calendarMetrics triple instead of the habit-metrics glossary (docs/43
 * ruling: non-habit ⓘ numbers mint their own locked copy chain). One
 * deliberate difference: no Analytics Reference Date caption — the calendar
 * is ARD-immune by design (D-P6-9), so the closing line states the clock
 * model that actually applies.
 */
import React, { useState } from 'react';
import { Box, IconButton, Popover, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { getCalendarMetricInfo } from '../calendar/calendarMetrics';
import { useLayoutTokens } from '../hooks/useLayoutTokens';

interface CalendarMetricInfoProps {
  /** calendarMetrics key, e.g. 'calendar_planned_minutes'. Unknown keys render nothing. */
  metric: string;
  size?: 'small' | 'medium';
}

const Line: React.FC<{ heading: string; children: React.ReactNode; mono?: boolean }> = ({
  heading,
  children,
  mono,
}) => (
  <Box sx={{ mb: 1 }}>
    <Typography variant="caption" color="text.secondary" display="block">
      {heading}
    </Typography>
    <Typography
      variant="body2"
      sx={mono ? { fontFamily: 'monospace', fontSize: '0.8rem' } : undefined}
    >
      {children}
    </Typography>
  </Box>
);

const CalendarMetricInfo: React.FC<CalendarMetricInfoProps> = ({
  metric,
  size = 'small',
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { panelPaddingSx } = useLayoutTokens();

  const info = getCalendarMetricInfo(metric);
  if (!info) {
    return null;
  }

  return (
    <>
      <IconButton
        size={size}
        aria-label={`About ${info.label}`}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={{ ml: 0.25 }}
      >
        <InfoOutlinedIcon fontSize="inherit" />
      </IconButton>
      <Popover
        open={anchorEl !== null}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={[panelPaddingSx, { maxWidth: 360 }]}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            {info.label}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {info.what}
          </Typography>
          <Line heading="Formula" mono>
            {info.formula}
          </Line>
          <Line heading="Window">{info.window}</Line>
          <Line heading="What counts as possible">{info.denominator}</Line>
          <Line heading="Current period">
            {info.includes_open_period
              ? 'Includes the period in progress right now.'
              : 'Excludes the period in progress right now — only fully-elapsed periods count.'}
          </Line>
          <Typography variant="caption" color="text.secondary" display="block">
            Computed server-side in your account timezone; the Analytics
            Reference Date never applies here.
          </Typography>
        </Box>
      </Popover>
    </>
  );
};

export default CalendarMetricInfo;
